<template>
  <div class="closed-jobs">
    <div v-if="loading">
      <div v-for="value in 3" :key="value" class="animate-pulse bg-grey-divider-subdued h-112 w-full mb-16" />
    </div>
    <div v-else-if="hasClosedJobs">
      <SeekerJobCard
        v-for="job in closedJobs"
        :key="job.id"
        :job="job"
        :account="account"
        class="mb-16"
        @click="handleJobClick"
        @manage-job="onManageJob"
      />
      <Button
        v-if="hasNextClosedPage"
        variant="secondary"
        class="cursor-pointer w-full mb-64 sm:flex sm:w-auto sm:mx-auto"
        :loading="loadingMore"
        @click.prevent="loadMore"
        >View More</Button
      >
    </div>
    <div v-else class="closed-jobs__empty">
      <h2 class="text-h4 w-full mb-8">No closed jobs</h2>
      <p class="text-body mb-24">Close your job when you've hired a sitter or no longer have a care need</p>
    </div>
    <ActionsPopup v-if="currentJob" v-model="showManagePopup" title="Manage Job" :buttons="manageJobButtons">
      <p v-if="currentJob?.title" class="text-body">{{ currentJob?.title }}</p>
    </ActionsPopup>
    <ActionsPopup v-if="currentJob" v-model="showCloseConfirmation" title="Close this job?" :buttons="closeConfirmationButtons">
      <p class="text-body">Once you close this job, new sitters will no longer be able to apply for it</p>
    </ActionsPopup>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import ActionsPopup from '@/components/actionsPopup/ActionsPopup.vue';
import type { ActionButton } from '@/components/actionsPopup/types';
import SeekerJobCard from '@/components/seekerJobCard/SeekerJobCard.vue';
import Button from '@/components/button/Button.vue';
import { redirect } from '@/helpers/redirects';
import { createViewApplicantsButton, createReopenJobButton, createCloseConfirmationButtons } from '@/helpers/jobActionButtons';
import { FETCH_CLOSED_JOBS } from '@/graphql/account/queries';

export default defineComponent({
  name: 'SeekerJobsClosed',
  components: {
    ActionsPopup,
    SeekerJobCard,
    Button,
  },
  apollo: {
    viewer: {
      query: FETCH_CLOSED_JOBS,
      result() {
        this.totalCount = this.viewer?.jobs?.totalCount ?? 0;
        this.lastPageInfo = this.viewer?.jobs?.pageInfo ?? null;
        this.closedJobs = (this.viewer?.jobs?.edges ?? []).map((edge: any) => edge.node);
        this.loading = false;
      },
      error() {
        this.loading = false;
      },
    },
  },
  data(): {
    totalCount: number;
    closedJobs: any[];
    loading: boolean;
    loadingMore: boolean;
    currentJob: any;
    showManagePopup: boolean;
    showCloseConfirmation: boolean;
    lastPageInfo: { hasNextPage: boolean; endCursor: string | null } | null;
  } {
    return {
      totalCount: 0,
      closedJobs: [],
      loading: true,
      loadingMore: false,
      currentJob: null,
      showManagePopup: false,
      showCloseConfirmation: false,
      lastPageInfo: null,
    };
  },
  computed: {
    closeConfirmationButtons(): ActionButton[] {
      return createCloseConfirmationButtons({
        onConfirm: () => this.closeJobMutation(),
        onCancel: () => this.cancelCloseConfirmation(),
      });
    },
    manageJobButtons(): ActionButton[] {
      const job = this.currentJob;
      if (!job) return [];
      const hasApplicants = job.applications.totalCount > 0;
      const currentUserOwnsJob = this.account.id === job.parent.id;

      const buttons: ActionButton[] = [];

      // the list of jobs returned for an account includes jobs that have been shared with the account
      // this checks that the user is the manager of the job
      if (currentUserOwnsJob) {
        buttons.push(createReopenJobButton(job.id));
      }

      if (hasApplicants) {
        buttons.push(createViewApplicantsButton(job.id));
      }
      return buttons;
    },
    hasClosedJobs(): boolean {
      return this.totalCount > 0;
    },
    account() {
      return this.$store.getters['account/getAccount'];
    },
    hasNextClosedPage(): boolean {
      if (this.lastPageInfo) return this.lastPageInfo.hasNextPage;
      return false;
    },
  },
  methods: {
    onManageJob(job: any) {
      this.currentJob = job;
      this.showManagePopup = true;
    },
    cancelCloseConfirmation() {
      this.showCloseConfirmation = false;
      this.showManagePopup = true;
    },
    async closeJobMutation() {
      this.showCloseConfirmation = false;
      if (!this.currentJob) return;
      const jobId = this.currentJob.id;
      try {
        await this.$store.dispatch('job/closeJob', jobId);
        redirect(`/jobs/action_with_uuid?job_action=job_closed_questionnaire&uuid=${jobId}`);
      } catch {
        // Error already logged in store action
      }
    },
    handleJobClick(card: any, job: any, event?: Event) {
      if (event) event.preventDefault();
      if (!job) return;

      this.currentJob = job;
      this.showManagePopup = true;
    },
    getLastCursor(): string | null {
      if (this.lastPageInfo?.endCursor) return this.lastPageInfo.endCursor;
      return null;
    },
    async loadMore() {
      const after = this.getLastCursor();
      if (!after || this.loadingMore) return;

      this.loadingMore = true;

      try {
        // Separate query (not the smart query's fetchMore) so the viewer query is never re-run.
        const { data } = await this.$apollo.query({
          query: FETCH_CLOSED_JOBS,
          variables: { after },
          fetchPolicy: 'network-only',
        });
        const jobs = data?.viewer?.jobs;
        this.lastPageInfo = jobs?.pageInfo ?? null;
        const newNodes = (jobs?.edges ?? []).map((edge: any) => edge.node);
        this.closedJobs = [...this.closedJobs, ...newNodes];
      } catch (error) {
        console.error('Error loading more closed jobs:', error);
      } finally {
        this.loadingMore = false;
      }
    },
  },
});
</script>
